import { useEffect, useRef, useState } from "react";
import { Globe } from "lucide-react";

import { useT } from "../../i18n";
import { useLocaleStore } from "../../store/localeStore";
import { KZ_FIT_PADDING } from "../../map/geo";
import { cx } from "../../lib/cx";
import { useDataStore } from "../../store/dataStore";
import { IconButton } from "../ui/IconButton";

/**
 * Region picker, next to the base-map picker. The backend lists the monitored
 * regions in /api/meta (Kazakhstan first, then Oman, ...) with a bbox each;
 * picking one flies the map there. With a single region there is nothing to
 * pick, so the control stays hidden and the map looks as it always did.
 */
export function RegionControl() {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Select the stored object, not a derived `?? []`: a fresh empty array on
  // every call is never equal to the previous one, so the store would
  // re-render this component forever (React error #185) until meta loads.
  const meta = useDataStore((s) => s.meta);
  const map = useDataStore((s) => s.map);
  const regions = meta?.regions ?? [];

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (regions.length < 2) return null;

  const flyTo = (bbox: [number, number, number, number]) => {
    const [west, south, east, north] = bbox;
    map?.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: KZ_FIT_PADDING },
    );
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <div data-map-overlay="true" className="rounded-lg border border-border-subtle bg-surface-raised/80 p-1 shadow-md backdrop-blur">
        <IconButton
          label={t.map.region}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={open ? "bg-accent-subtle text-accent" : undefined}
        >
          <Globe size={18} />
        </IconButton>
      </div>

      {open && (
        <div
          role="menu"
          aria-label={t.map.region}
          data-map-overlay="true"
          className="absolute right-full top-0 mr-2 w-48 rounded-panel border border-border-subtle bg-surface-raised/95 p-1.5 shadow-md backdrop-blur"
        >
          {regions.map((r) => (
            <button
              key={r.key}
              type="button"
              role="menuitem"
              onClick={() => flyTo(r.bbox)}
              className={cx(
                "flex w-full items-center rounded px-1.5 py-1 text-left text-sm text-text-primary hover:bg-surface-sunken",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
              )}
            >
              {r.names[locale]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
